/**
 * TemporalDistributionPanel
 *
 * Reusable temporal-distribution chart extracted verbatim from Report Clustering's
 * "Temporal Distribution" view. Each entity is "a key with a list of event timestamps
 * plus a recent-vs-prior count trend" — the panel knows nothing about clusters, so it
 * can later serve barriers, sources, etc.
 *
 * The caller supplies the already-sorted, already-min-count-filtered entity list. The
 * panel applies the trend-chip filter and the "Show top N" range slice internally, and
 * surfaces hover / click as callbacks so the parent can drive list navigation.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {Chart as ChartJS} from 'chart.js';
import {Scatter} from 'react-chartjs-2';
import {hexToRgba} from '../../../utils/chartColors';
import {drawTrendWindowBand, drawTrendWindowDivider, drawTrendWindowPill} from '../../shared/trendWindowOverlay';
import {TrendWindow} from '../../shared/TrendWindowSelect';
import {TrendWindowBoundary} from '../../../utils/trendWindowBoundary';
import {Bucketed, CountTrendStats, LaplaceTrend, TemporalSort, bucketEndOf, bucketLabelOf, bucketStartOf, formatTrendStats, laplaceTrend, nextBucketStart} from '../../../utils/temporalDistribution';

export type TemporalView = 'points' | 'sparkline' | 'boxplot';
export type TemporalGranularity = 'normal' | 'month' | 'quarter' | 'year';
// Re-exported from the util layer (where the comparator factory lives) so existing
// importers of `TemporalSort` from this panel keep working without a circular import.
export type {TemporalSort};

/** Generic entity contract for the temporal panel. The caller adapts its domain type
 *  (e.g. a cluster) into this shape, computing labels and pre-extracting valid event
 *  timestamps. */
export interface TemporalEntity {
    /** Stable identity key (RC: cluster.signatureKey). */
    key: string;
    /** Count-rank label, e.g. "C1" — caller computes. */
    label: string;
    /** Tooltip title detail (RC: failed barriers joined by ", "). */
    title: string;
    /** Valid event timestamps (ms): report dates filtered to (dateTime && !isNaN). */
    events: number[];
    /** Total report count (RC: cluster.count). */
    count: number;
    /** Recent-vs-prior count trend (RC: clusterTrendStats.get(key)). */
    trend: CountTrendStats;
}

interface TemporalDistributionPanelProps {
    /** ALREADY sorted by the caller's comparator AND min-count-filtered; trend-filter &
     *  range are NOT applied (the panel does those). */
    entities: TemporalEntity[];
    /** Total entity count before any slicing (RC: clusters.length) — for the
     *  "Show top N of M" control and caption. */
    totalEntityCount: number;
    trendWindow: TrendWindowBoundary | null;
    trendWindowMode: TrendWindow;
    rangeFrom: Date | null;
    rangeTo: Date | null;
    sort: TemporalSort;
    onSortChange(s: TemporalSort): void;
    minClusterCount: number;
    onMinClusterCountChange(n: number): void;
    hoveredKey: string | null;
    onHoverKey(key: string | null): void;
    onEntityClick(key: string): void;
    /** Noun for the "of N clusters" caption / axis title / sort label — defaults to "cluster". */
    entityNoun?: string;
    /** Noun for the individual events plotted (RC: a report) — defaults to "report". */
    eventNoun?: string;
}

interface BoxStats {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
    count: number;
}

/** Milliseconds per day — for inter-event gap conversion (gap boxplot is in days, to
 *  match the rest of the time-to-failure feature and avoid rounding day-scale gaps to 0). */
const MS_PER_DAY = 86400000;

/** Linear-interpolated quantile of an ascending-sorted numeric array. */
function quantileSorted(sorted: number[], q: number): number {
    if (sorted.length === 0) return NaN;
    if (sorted.length === 1) return sorted[0];
    const pos = (sorted.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;
    return sorted[base] + rest * ((sorted[base + 1] ?? sorted[base]) - sorted[base]);
}

const CLUSTER_COLOR = '#ef5350';

/** Max per-point rows listed in a tooltip before collapsing the remainder to "+N more"
 *  — a high-failure row otherwise produces a tooltip taller than the chart. */
const TOOLTIP_MAX_ITEMS = 8;

// Trend palette for Dotplot — colors each entity's dots by whether the failure pattern
// is getting worse (rising = red), improving (falling = green), unchanged (stable = grey),
// or insufficient data (null = light grey).
const TREND_COLORS = {rising: '#d32f2f', stable: '#9e9e9e', falling: '#2e7d32', unknown: '#bdbdbd'} as const;
const trendColor = (trend: 'rising' | 'falling' | 'stable' | null | undefined): string => {
    if (trend === 'rising') return TREND_COLORS.rising;
    if (trend === 'falling') return TREND_COLORS.falling;
    if (trend === 'stable') return TREND_COLORS.stable;
    return TREND_COLORS.unknown;
};

const TemporalDistributionPanel: React.FC<TemporalDistributionPanelProps> = ({
    entities,
    totalEntityCount,
    trendWindow,
    trendWindowMode,
    rangeFrom,
    rangeTo,
    sort,
    onSortChange,
    minClusterCount,
    onMinClusterCountChange,
    hoveredKey,
    onHoverKey,
    onEntityClick,
    entityNoun = 'cluster',
    eventNoun = 'report',
}) => {
    // Naive pluralization for count strings — "1 report" / "2 reports".
    const plural = (n: number, noun: string): string => `${n} ${noun}${n !== 1 ? 's' : ''}`;
    // Title-cased nouns for axis titles ("Cluster" / "Barrier", "Report" / "Failure").
    const entityNounTitle = entityNoun.charAt(0).toUpperCase() + entityNoun.slice(1);
    const eventNounTitle = eventNoun.charAt(0).toUpperCase() + eventNoun.slice(1);
    // The tooltip title can be a long comma-joined list (e.g. an RC cluster's many failed
    // barriers). Show the first few and summarize the rest as "+N more" so the tooltip
    // stays readable. Single-item titles (e.g. a BE barrier name) pass through untouched.
    const TOOLTIP_TITLE_MAX = 8;
    const formatRowTitle = (label: string, title: string): string => {
        if (!title) return label;
        const parts = title.split(', ');
        if (parts.length <= TOOLTIP_TITLE_MAX) return `${label}: ${title}`;
        return `${label}: ${parts.slice(0, TOOLTIP_TITLE_MAX).join(', ')} +${parts.length - TOOLTIP_TITLE_MAX} more`;
    };
    const [temporalView, setTemporalView] = useState<TemporalView>('points');
    const [temporalGranularity, setTemporalGranularity] = useState<TemporalGranularity>('normal');
    const [boxGapMode, setBoxGapMode] = useState(false);
    // Two coloring lenses, mutually exclusive:
    //  - 'window'  → recent-vs-prior count trend within the selected trend window (CountTrendStats).
    //  - 'laplace' → distribution-free Laplace centroid test over the entity's whole history
    //                (failure-rate accelerating / decelerating), needs no window.
    //  - 'none'    → no coloring (default; identical to the old colorByTrend=false).
    const [colorMode, setColorMode] = useState<'none' | 'window' | 'laplace'>('none');
    // Derived so all existing `colorByTrend` reads (layout padding, chip gating, etc.) keep working.
    const colorByTrend = colorMode !== 'none';
    const [hideOutOfWindow, setHideOutOfWindow] = useState(true);
    // Trend-category filter driven by clicking legend chips. Empty = show all (default).
    const [temporalTrendFilter, setTemporalTrendFilter] = useState<Set<'rising' | 'stable' | 'falling'>>(new Set());
    const [temporalRangeMode, setTemporalRangeMode] = useState<number | 'custom'>(30);
    const [temporalStart, setTemporalStart] = useState(1);
    const [temporalEnd, setTemporalEnd] = useState(30);

    // Hover-sync plumbing — the dim happens at draw time via a Chart.js plugin reading
    // these refs, so changing the hovered entity does NOT rebuild `temporalData` (which
    // had caused a thrashing "walls of dots" repaint when scrubbing across dots).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const temporalChartRef = useRef<any>(null);
    const hoveredKeyRef = useRef<string | null>(null);
    const datasetClusterKeysRef = useRef<string[]>([]);
    // Full count of points behind the current tooltip (before the "+N more" cap) — set in
    // the tooltip `filter`, read in `afterBody`. A ref avoids restructuring the options memo.
    const tooltipTotalRef = useRef(0);

    // Laplace centroid test per entity over its FULL history — only computed in the
    // 'laplace' lens (else an empty Map keeps it cheap). The observation window is the
    // explicit date range if both bounds are set, otherwise the min/max event timestamp
    // across all entities.
    const laplaceByKey = useMemo((): Map<string, LaplaceTrend> => {
        const map = new Map<string, LaplaceTrend>();
        if (colorMode !== 'laplace') return map;
        let windowStart: number;
        let windowEnd: number;
        if (rangeFrom && rangeTo) {
            windowStart = rangeFrom.getTime();
            windowEnd = rangeTo.getTime();
        } else {
            const all = entities.flatMap((e) => e.events);
            if (all.length === 0) return map;
            windowStart = Math.min(...all);
            windowEnd = Math.max(...all);
        }
        entities.forEach((e) => {
            map.set(e.key, laplaceTrend(e.events, windowStart, windowEnd));
        });
        return map;
    }, [entities, colorMode, rangeFrom, rangeTo]);

    // Resolve an entity's coloring verdict for the active lens, mapped onto the shared
    // rising/stable/falling internal keys so trendColor / the filter Set / chips are reused
    // unchanged. Laplace 'deteriorating' shares the red 'rising' swatch, 'improving' the
    // green 'falling'.
    const verdictFor = (e: TemporalEntity): 'rising' | 'stable' | 'falling' | null => {
        if (colorMode === 'window') return e.trend.trend;
        if (colorMode === 'laplace') {
            const v = laplaceByKey.get(e.key)?.verdict;
            if (v === 'deteriorating') return 'rising';
            if (v === 'improving') return 'falling';
            if (v === 'stable') return 'stable';
            return null;
        }
        return null;
    };

    // Post-trend-filter, pre-slice view of the entity list. Length is exposed in the chart
    // controls as the "filtered out of total" count so the analyst can tell how many
    // entities the current trend-chip filter actually keeps. (min-count is already applied
    // by the caller.)
    const filtered = useMemo((): TemporalEntity[] => {
        if (colorByTrend && temporalTrendFilter.size > 0) {
            return entities.filter((e) => {
                const trend = verdictFor(e);
                return trend != null && temporalTrendFilter.has(trend);
            });
        }
        return entities;
    }, [entities, colorByTrend, colorMode, laplaceByKey, temporalTrendFilter]);

    const visible = useMemo((): TemporalEntity[] => {
        if (temporalRangeMode === 'custom') {
            return filtered.slice(temporalStart - 1, Math.min(temporalEnd, filtered.length));
        }
        return filtered.slice(0, temporalRangeMode);
    }, [filtered, temporalRangeMode, temporalStart, temporalEnd]);

    // Keep the dataset-index → key lookup in a ref so the dim plugin can map
    // Chart.js dataset indices back to entities at draw time without closing over state.
    useEffect(() => {
        datasetClusterKeysRef.current = visible.map((e) => e.key);
    }, [visible]);

    // Mirror the hovered key into a ref and trigger a no-animation redraw. The chart
    // never rebuilds its data — only the plugin's draw-time globalAlpha changes.
    useEffect(() => {
        hoveredKeyRef.current = hoveredKey;
        temporalChartRef.current?.update?.('none');
    }, [hoveredKey]);

    // 1-based count-rank label for a Dotplot row. Always reflects the entity's identity,
    // so labels match the entity list below the chart regardless of the chart's current sort.
    const labelForRow = (rowIndex: number): string => visible[rowIndex]?.label ?? '';

    // Sparkline always needs bucketing — when the user has "Group by" set to None we
    // default to monthly so the line has meaningful resolution. Declared here (before
    // temporalOptions) so the X-axis bucketing can read it without a TDZ error.
    const sparklineGranularity: Bucketed = temporalGranularity === 'normal' ? 'month' : temporalGranularity;

    const temporalData = useMemo(() => {
        // One dataset per entity; x = timestamp (ms), y = entity index.
        // When hideOutOfWindow is on, dots outside the trend window are hidden
        // (radius 0) regardless of color-by-trend; otherwise every dot renders.
        const window = trendWindow;
        const inWindow = (ctx: {raw?: unknown}): boolean => {
            if (!window) return true;
            const t = (ctx.raw as {x?: number} | undefined)?.x;
            return t != null && t >= window.start && t <= window.end;
        };
        const datasets = visible.map((entity, i) => {
            const points = entity.events.map((t) => ({x: t, y: i}));
            const base = colorByTrend ? trendColor(verdictFor(entity)) : CLUSTER_COLOR;
            return {
                data: points,
                backgroundColor: base,
                pointRadius: !window || !hideOutOfWindow
                    ? 5
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    : (ctx: any): number => (inWindow(ctx) ? 5 : 0),
                pointHoverRadius: !window || !hideOutOfWindow
                    ? 7
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    : (ctx: any): number => (inWindow(ctx) ? 7 : 0),
                showLine: false,
            };
        });
        return {datasets};
    }, [visible, colorByTrend, colorMode, laplaceByKey, trendWindow, hideOutOfWindow]);

    const temporalOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        // Reserve a strip above the plot for the trend-split pill so it doesn't cover dots.
        layout: {padding: {top: colorByTrend && trendWindow ? 20 : 0}},
        plugins: {
            legend: {display: false},
            // Channel for the trend-window overlay plugin. The window selector — not
            // color-by-trend — drives the split divider and band, so analysts can use the
            // window to focus the chart without committing to the trend palette.
            trendWindowOverlay: {
                window: trendWindow,
                // Suppress the band when out-of-window dots are already hidden (band would
                // be redundant) or in mid-point mode (band fills the whole chart anyway).
                showBand: !!trendWindow && !hideOutOfWindow && trendWindowMode !== 'midpoint',
                // Pill label: window size + the divider's calendar date so analysts can
                // read the split boundary without inspecting the X axis. Mirrors the
                // BarrierEffectivenessTab pill format for cross-tab consistency.
                label: trendWindow
                    ? (((): string => {
                        const windowLabel = trendWindowMode === 'midpoint' ? 'Mid-point' : `${trendWindowMode} mo`;
                        const dateLabel = new Date(trendWindow.recentStart).toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
                        return `${windowLabel} · ${dateLabel}`;
                    })())
                    : '',
            },
            tooltip: {
                // Anchor the tooltip BELOW the cursor (caret at the top of the box, box
                // extends downward) so it never covers the hovered entity's row of dots.
                // Pinned xAlign keeps it from jittering left/right on quick scrubs.
                yAlign: 'top' as const,
                xAlign: 'center' as const,
                caretPadding: 8,
                // A high-failure row surfaces many points at once; cap the per-point list
                // (and record the full count) so the tooltip never grows taller than the
                // chart — the remainder is summarized by `afterBody` as "+N more".
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                filter: (_item: any, index: number, items: any[]): boolean => {
                    tooltipTotalRef.current = items.length;
                    return index < TOOLTIP_MAX_ITEMS;
                },
                callbacks: {
                    title: (items: { dataIndex: number; datasetIndex: number }[]): string => {
                        const {datasetIndex} = items[0] ?? {};
                        if (datasetIndex == null || !visible[datasetIndex]) return '';
                        return formatRowTitle(labelForRow(datasetIndex), visible[datasetIndex].title);
                    },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (item: any): string => {
                        const ts = item.parsed?.x;
                        if (!ts) return '';
                        // Sparkline and bucketed Dotplot points carry a `count` — show the
                        // bucket label (e.g. "Q1 2008") and count rather than a midpoint date.
                        const count = item.raw?.count;
                        const bucket: Bucketed | null = temporalView === 'sparkline'
                            ? sparklineGranularity
                            : (temporalGranularity !== 'normal' ? temporalGranularity : null);
                        if (count != null && bucket) {
                            return `${plural(count, eventNoun)} — ${bucketLabelOf(ts, bucket)}`;
                        }
                        const d = new Date(ts);
                        return d.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'});
                    },
                    // Summarize the points the `filter` cap dropped.
                    afterBody: (): string => {
                        const extra = tooltipTotalRef.current - TOOLTIP_MAX_ITEMS;
                        return extra > 0 ? `+${extra} more` : '';
                    },
                    // Trend stats line — only shows when color-by-trend is on and the
                    // entity has a non-null trend signal. Tells the user the raw
                    // recent/prior counts behind the rising/stable/falling verdict.
                    footer: (items: { datasetIndex: number }[]): string => {
                        if (colorMode === 'none') return '';
                        const {datasetIndex} = items[0] ?? {};
                        const entity = datasetIndex == null ? undefined : visible[datasetIndex];
                        if (!entity) return '';
                        if (colorMode === 'laplace') {
                            const lap = laplaceByKey.get(entity.key);
                            if (!lap?.verdict) return '';
                            const word = lap.verdict.charAt(0).toUpperCase() + lap.verdict.slice(1);
                            return `${word}: Laplace U = ${lap.u.toFixed(2)} over ${plural(lap.n, eventNoun)}`;
                        }
                        const stats = entity.trend;
                        if (!stats?.trend) return '';
                        const trendWord = stats.trend === 'rising' ? 'Rising' : stats.trend === 'falling' ? 'Falling' : 'Stable';
                        const line = formatTrendStats(stats, trendWindowMode);
                        return line ? `${trendWord}: ${line}` : '';
                    },
                },
            },
        },
        scales: {
            x: {
                type: 'time' as const,
                // Reads newest → oldest (left to right) to match the Barrier Effectiveness
                // timeline. Cross-tab consistency: an analyst flipping between tabs sees
                // recent activity on the left in both places.
                reverse: true,
                // Pad both edges in Density so edge bubbles aren't clipped to half.
                offset: (temporalView === 'points' && temporalGranularity !== 'normal') || temporalView === 'sparkline',
                time: {
                    // In Density and Sparkline views, align ticks to the chosen bucket so labels match the columns.
                    unit: temporalView === 'sparkline'
                        ? sparklineGranularity
                        : (temporalView === 'points' && temporalGranularity !== 'normal' ? temporalGranularity : undefined),
                    tooltipFormat: 'MMM d, yyyy',
                    displayFormats: {
                        day: 'MMM d, yyyy',
                        week: 'MMM d, yyyy',
                        month: 'MMM yyyy',
                        quarter: 'QQQ yyyy',
                        year: 'yyyy',
                    },
                },
                // When hiding out-of-window dots, collapse the axis to the window so the
                // visible activity fills the canvas instead of sitting next to empty space.
                min: hideOutOfWindow && trendWindow
                    ? trendWindow.start
                    : (rangeFrom
                        ? (temporalView === 'sparkline'
                            ? bucketStartOf(rangeFrom, sparklineGranularity)
                            : (temporalView === 'points' && temporalGranularity !== 'normal'
                                ? bucketStartOf(rangeFrom, temporalGranularity)
                                : rangeFrom.getTime()))
                        : undefined),
                max: hideOutOfWindow && trendWindow
                    ? trendWindow.end
                    : (rangeTo
                        ? (temporalView === 'sparkline'
                            ? bucketEndOf(rangeTo, sparklineGranularity)
                            : (temporalView === 'points' && temporalGranularity !== 'normal'
                                ? bucketEndOf(rangeTo, temporalGranularity)
                                : rangeTo.getTime()))
                        : undefined),
                ticks: {display: true, autoSkip: true, maxTicksLimit: temporalView === 'points' && temporalGranularity === 'year' ? 20 : 8},
                title: {display: true, text: 'Date'},
            },
            y: {
                type: 'linear' as const,
                title: {display: true, text: entityNounTitle},
                reverse: true,
                afterBuildTicks: (axis: { ticks: { value: number }[] }): void => {
                    axis.ticks = visible.map((_, i) => ({value: i}));
                },
                ticks: {
                    callback: (value: number | string): string => {
                        const v = Number(value);
                        return v >= 0 && v < visible.length ? labelForRow(v) : '';
                    },
                },
                // Sparkline peaks reach `i - 0.7`; widen the top of the Y axis to ~0.8
                // so the topmost entity's peak (i=0 → y=-0.7) isn't clipped at -0.5.
                min: temporalView === 'sparkline' ? -0.8 : -0.5,
                max: Math.max(0, visible.length - 1) + 0.5,
            },
        },
        // Hover sync — mirrors the cursor's entity into shared state so the matching
        // list row gets a background tint. Guarded by a value-equality check so the
        // callback doesn't churn state on every mousemove event.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onHover: (_e: unknown, elements: Array<{datasetIndex: number}>, _chart: any): void => {
            const key = elements.length > 0
                ? (visible[elements[0].datasetIndex]?.key ?? null)
                : null;
            onHoverKey(key);
        },
        // Click a bubble to navigate to and expand its entity in the list below.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick: (_e: unknown, elements: Array<{datasetIndex: number}>, _chart: any): void => {
            if (elements.length === 0) return;
            const clicked = visible[elements[0].datasetIndex];
            if (!clicked) return;
            onEntityClick(clicked.key);
        },
    }), [visible, temporalView, temporalGranularity, rangeFrom, rangeTo, colorByTrend, colorMode, laplaceByKey, trendWindow, hideOutOfWindow, trendWindowMode, onHoverKey, onEntityClick, sparklineGranularity, eventNoun, entityNounTitle]);

    // Per-entity report counts bucketed by the selected granularity — backs the bucketed
    // Dotplot modes (Month/Quarter/Year). Skipped entirely in "Normal" mode.
    const temporalBinned = useMemo((): Array<Array<{ts: number; count: number}>> => {
        if (temporalGranularity === 'normal') return [];
        const granularity = temporalGranularity;
        return visible.map((entity) => {
            const counts = new Map<number, number>();
            entity.events.forEach((t) => {
                const ts = bucketStartOf(new Date(t), granularity);
                counts.set(ts, (counts.get(ts) ?? 0) + 1);
            });
            return Array.from(counts.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([ts, count]) => ({ts, count}));
        });
    }, [visible, temporalGranularity]);

    // Density: one bubble per (entity, bucket); count encoded by radius.
    const temporalDensityData = useMemo(() => {
        let maxCount = 1;
        temporalBinned.forEach((bins) => bins.forEach((b) => { if (b.count > maxCount) maxCount = b.count; }));
        const window = trendWindow;
        const inWindow = (ctx: {raw?: unknown}): boolean => {
            if (!window) return true;
            const t = (ctx.raw as {x?: number} | undefined)?.x;
            return t != null && t >= window.start && t <= window.end;
        };
        const datasets = temporalBinned.map((bins, i) => {
            const entity = visible[i];
            const base = colorByTrend ? trendColor(entity ? verdictFor(entity) : null) : CLUSTER_COLOR;
            return {
                data: bins.map((b) => ({x: b.ts, y: i, count: b.count})),
                backgroundColor: base,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pointRadius: (ctx: any): number => {
                    if (window && hideOutOfWindow && !inWindow(ctx)) return 0;
                    return 3 + 7 * Math.sqrt((ctx.raw?.count ?? 1) / maxCount);
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                pointHoverRadius: (ctx: any): number => {
                    if (window && hideOutOfWindow && !inWindow(ctx)) return 0;
                    return 5 + 7 * Math.sqrt((ctx.raw?.count ?? 1) / maxCount);
                },
                showLine: false,
            };
        });
        return {datasets};
    }, [temporalBinned, visible, colorByTrend, colorMode, laplaceByKey, trendWindow, hideOutOfWindow]);

    // Per-entity bucketed counts for the Sparkline view. Zero-filled from the
    // entity's first active bucket to the global "now" reference (`trendWindow.end`),
    // so recent quiet buckets visibly drop to baseline. Without the right-edge
    // extension, an entity whose last report was 3 months ago would silently end at
    // that bucket — analysts would see no signal that the entity has gone quiet.
    const temporalSparklineBinned = useMemo((): Array<Array<{ts: number; count: number}>> => {
        const rightEdgeBucket = trendWindow
            ? bucketStartOf(new Date(trendWindow.end), sparklineGranularity)
            : null;
        return visible.map((entity) => {
            const counts = new Map<number, number>();
            entity.events.forEach((t) => {
                const ts = bucketStartOf(new Date(t), sparklineGranularity);
                counts.set(ts, (counts.get(ts) ?? 0) + 1);
            });
            if (counts.size === 0) return [];
            const sortedTs = Array.from(counts.keys()).sort((a, b) => a - b);
            const first = sortedTs[0];
            const clusterLast = sortedTs[sortedTs.length - 1];
            const last = rightEdgeBucket != null ? Math.max(clusterLast, rightEdgeBucket) : clusterLast;
            const filled: Array<{ts: number; count: number}> = [];
            for (let ts = first; ts <= last; ts = nextBucketStart(ts, sparklineGranularity)) {
                filled.push({ts, count: counts.get(ts) ?? 0});
            }
            return filled;
        });
    }, [visible, sparklineGranularity, trendWindow]);

    // Sparkline data: per-row mini line chart of monthly (or chosen-granularity) counts.
    // Per-row normalized — each entity's peak fills 80% of its row band. Optimizes for
    // shape comparison (when did this entity spike?) at the cost of cross-row magnitude.
    const temporalSparklineData = useMemo(() => {
        const datasets = temporalSparklineBinned.map((bins, i) => {
            const entity = visible[i];
            const maxCount = Math.max(1, ...bins.map((b) => b.count));
            const base = colorByTrend ? trendColor(entity ? verdictFor(entity) : null) : CLUSTER_COLOR;
            // Render as a per-row area chart filling from baseline (y=i, the entity
            // label line) upward to the peak (y=i-0.7). 0.7 of the row band gives
            // enough vertical resolution to see small variations; semi-transparent
            // fill makes the "shape" of activity legible at a glance without dominating
            // adjacent rows.
            const baselineY = i;
            return {
                data: bins.map((b) => ({x: b.ts, y: i - 0.7 * (b.count / maxCount), count: b.count})),
                backgroundColor: hexToRgba(base, 0.35),
                borderColor: base,
                borderWidth: 1.5,
                pointRadius: 0,
                pointHoverRadius: 4,
                showLine: true,
                // Straight segments (no smoothing) so single-bucket spikes show as actual
                // spikes rather than getting blurred into rounded humps.
                tension: 0,
                // Fill down to the row's baseline (y=i) — Chart.js accepts a number for the
                // fill target y-value, producing an area chart per dataset.
                fill: {target: {value: baselineY}, above: hexToRgba(base, 0.35)},
            };
        });
        return {datasets};
    }, [temporalSparklineBinned, visible, colorByTrend, colorMode, laplaceByKey]);

    // Boxplot (date mode): per-entity distribution of report dates.
    const temporalBoxData = useMemo((): Array<BoxStats | null> => {
        return visible.map((entity) => {
            const ts = [...entity.events].sort((a, b) => a - b);
            if (ts.length === 0) return null;
            return {
                min: ts[0],
                max: ts[ts.length - 1],
                q1: quantileSorted(ts, 0.25),
                median: quantileSorted(ts, 0.5),
                q3: quantileSorted(ts, 0.75),
                count: ts.length,
            };
        });
    }, [visible]);

    // Boxplot (gap mode): per-entity distribution of days between consecutive events.
    const temporalBoxGapData = useMemo((): Array<BoxStats | null> => {
        return visible.map((entity) => {
            const ts = [...entity.events].sort((a, b) => a - b);
            if (ts.length < 2) return null; // need ≥2 events for a gap
            const gaps: number[] = [];
            for (let i = 1; i < ts.length; i++) gaps.push((ts[i] - ts[i - 1]) / MS_PER_DAY);
            gaps.sort((a, b) => a - b);
            return {
                min: gaps[0],
                max: gaps[gaps.length - 1],
                q1: quantileSorted(gaps, 0.25),
                median: quantileSorted(gaps, 0.5),
                q3: quantileSorted(gaps, 0.75),
                count: gaps.length,
            };
        });
    }, [visible]);

    const activeBoxData = boxGapMode ? temporalBoxGapData : temporalBoxData;

    const boxYRange = useMemo((): {min: number; max: number} | undefined => {
        let min = Infinity;
        let max = -Infinity;
        activeBoxData.forEach((b) => { if (b) { if (b.min < min) min = b.min; if (b.max > max) max = b.max; } });
        if (!isFinite(min) || !isFinite(max)) return undefined;
        const pad = (max - min) * 0.05 || (boxGapMode ? 1 : 86400000);
        return {min: boxGapMode ? Math.max(0, min - pad) : min - pad, max: max + pad};
    }, [activeBoxData, boxGapMode]);

    // Invisible hover anchors at each entity's median, carrying its stats for the tooltip.
    const temporalBoxBaseData = useMemo(() => ({
        datasets: [{
            data: activeBoxData
                .map((b, i) => (b ? {x: i, y: b.median, box: b, idx: i} : null))
                .filter((p): p is {x: number; y: number; box: BoxStats; idx: number} => p != null),
            pointRadius: 0,
            pointHitRadius: 14,
            backgroundColor: CLUSTER_COLOR,
        }],
    }), [activeBoxData]);

    const temporalBoxOptions = useMemo(() => {
        const fmtDate = (ts: number): string => new Date(ts).toLocaleDateString('en-US', {year: 'numeric', month: 'short'});
        const fmtDays = (d: number): string => `${Math.round(d)}d`;
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {display: false},
                // Channel for the custom box-drawing plugin.
                clusterBoxplot: {boxData: activeBoxData},
                tooltip: {
                    callbacks: {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        title: (items: any[]): string => {
                            const idx = items[0]?.raw?.idx;
                            if (idx == null || !visible[idx]) return '';
                            return formatRowTitle(labelForRow(idx), visible[idx].title);
                        },
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        label: (item: any): string[] => {
                            const b: BoxStats | undefined = item.raw?.box;
                            if (!b) return [];
                            if (boxGapMode) {
                                return [
                                    plural(b.count + 1, eventNoun),
                                    `Median gap: ${fmtDays(b.median)}`,
                                    `IQR: ${fmtDays(b.q1)} – ${fmtDays(b.q3)}`,
                                    `Range: ${fmtDays(b.min)} – ${fmtDays(b.max)}`,
                                ];
                            }
                            return [
                                plural(b.count, eventNoun),
                                `Median: ${fmtDate(b.median)}`,
                                `IQR: ${fmtDate(b.q1)} – ${fmtDate(b.q3)}`,
                                `Range: ${fmtDate(b.min)} – ${fmtDate(b.max)}`,
                            ];
                        },
                    },
                },
            },
            scales: {
                x: {
                    type: 'linear' as const,
                    min: -0.5,
                    max: Math.max(0, visible.length - 1) + 0.5,
                    ticks: {
                        stepSize: 1,
                        autoSkip: true,
                        maxTicksLimit: 30,
                        callback: (value: number | string): string => {
                            const i = Number(value);
                            return Number.isInteger(i) && i >= 0 && i < visible.length ? labelForRow(i) : '';
                        },
                    },
                    title: {display: true, text: entityNounTitle},
                },
                y: boxGapMode
                    ? {
                        type: 'linear' as const,
                        min: boxYRange?.min,
                        max: boxYRange?.max,
                        beginAtZero: true,
                        title: {display: true, text: `Days between ${eventNoun}s`},
                    }
                    : {
                        type: 'time' as const,
                        min: boxYRange?.min,
                        max: boxYRange?.max,
                        time: {displayFormats: {month: 'MMM yyyy', quarter: 'QQQ yyyy', year: 'yyyy'}},
                        title: {display: true, text: `${eventNounTitle} date`},
                    },
            },
        };
    }, [visible, activeBoxData, boxYRange, boxGapMode, eventNoun, entityNounTitle, eventNounTitle]);

    // Inline plugin: draws box-and-whiskers per entity from the stats in chart options.
    const boxPlugin = useMemo(() => ({
        id: 'clusterBoxplot',
        afterDatasetsDraw(chart: ChartJS): void {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const boxData = (chart.options.plugins as any)?.clusterBoxplot?.boxData as Array<BoxStats | null> | undefined;
            const x = chart.scales['x'];
            const y = chart.scales['y'];
            if (!boxData || !x || !y) return;
            const ctx = chart.ctx;
            const slot = Math.abs(x.getPixelForValue(1) - x.getPixelForValue(0)) || 40;
            const boxW = Math.min(30, slot * 0.5);
            ctx.save();
            ctx.strokeStyle = '#c62828';
            ctx.lineWidth = 1.25;
            boxData.forEach((b, i) => {
                if (!b) return;
                const cx = x.getPixelForValue(i);
                const yMin = y.getPixelForValue(b.min);
                const yMax = y.getPixelForValue(b.max);
                const yQ1 = y.getPixelForValue(b.q1);
                const yQ3 = y.getPixelForValue(b.q3);
                const yMed = y.getPixelForValue(b.median);
                ctx.beginPath();
                ctx.moveTo(cx, yMin);
                ctx.lineTo(cx, yMax);
                ctx.moveTo(cx - boxW / 3, yMin);
                ctx.lineTo(cx + boxW / 3, yMin);
                ctx.moveTo(cx - boxW / 3, yMax);
                ctx.lineTo(cx + boxW / 3, yMax);
                ctx.stroke();
                const top = Math.min(yQ1, yQ3);
                const h = Math.max(1, Math.abs(yQ1 - yQ3));
                ctx.fillStyle = 'rgba(239, 83, 80, 0.30)';
                ctx.fillRect(cx - boxW / 2, top, boxW, h);
                ctx.strokeRect(cx - boxW / 2, top, boxW, h);
                ctx.beginPath();
                ctx.moveTo(cx - boxW / 2, yMed);
                ctx.lineTo(cx + boxW / 2, yMed);
                ctx.stroke();
            });
            ctx.restore();
        },
    }), []);

    // Hover-sync dim plugin. Reads the hovered entity key from a ref (NOT React state),
    // so dim updates do not rebuild `temporalData`. Wraps each non-matching dataset's
    // draw in save()/globalAlpha/restore(); the matching entity's dataset draws at full
    // opacity.
    const hoverDimPlugin = useMemo(() => ({
        id: 'hoverDim',
        beforeDatasetDraw(chart: ChartJS, args: {index: number}): void {
            const hovered = hoveredKeyRef.current;
            if (!hovered) return;
            const datasetKey = datasetClusterKeysRef.current[args.index];
            if (!datasetKey || datasetKey === hovered) return;
            chart.ctx.save();
            chart.ctx.globalAlpha = 0.15;
        },
        afterDatasetDraw(chart: ChartJS, args: {index: number}): void {
            const hovered = hoveredKeyRef.current;
            if (!hovered) return;
            const datasetKey = datasetClusterKeysRef.current[args.index];
            if (!datasetKey || datasetKey === hovered) return;
            chart.ctx.restore();
        },
    }), []);

    // Draws a subtle band over the Dotplot's trend window and a prominent divider at the
    // prior/recent split. The divider gets a white halo + amber line + a top pill so it
    // reads clearly across the entity dots and the blue band.
    const trendWindowPlugin = useMemo(() => ({
        id: 'trendWindowOverlay',
        afterDatasetsDraw(chart: ChartJS): void {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const channel = (chart.options.plugins as any)?.trendWindowOverlay;
            const window = channel?.window as {start: number; recentStart: number; end: number} | null | undefined;
            if (!window) return;
            const x = chart.scales['x'];
            if (!x) return;
            const {chartArea} = chart;
            const ctx = chart.ctx;
            const wSplit = x.getPixelForValue(window.recentStart);
            if (wSplit <= chartArea.left || wSplit >= chartArea.right) return;
            ctx.save();
            drawTrendWindowDivider(ctx, wSplit, chartArea.top, chartArea.bottom);
            // Top pill labelling the divider with window size + calendar date so analysts
            // see what the split represents without inspecting the X axis. Sits in the
            // padding strip above the plot area (reserved via layout.padding.top) so it
            // never covers dots.
            const label = (channel?.label as string | undefined) || 'split';
            drawTrendWindowPill(ctx, wSplit, chartArea.top, chartArea.left, chartArea.right, label);
            ctx.restore();
        },
        beforeDatasetsDraw(chart: ChartJS): void {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const channel = (chart.options.plugins as any)?.trendWindowOverlay;
            const window = channel?.window as {start: number; recentStart: number; end: number} | null | undefined;
            if (!window || !channel?.showBand) return;
            const x = chart.scales['x'];
            if (!x) return;
            const {chartArea} = chart;
            const ctx = chart.ctx;
            // wStart / wEnd are pixel positions for the older / newer band edges. With
            // `reverse: true` on the X axis, wStart sits to the RIGHT of wEnd, so the rect
            // math takes the min/max rather than assuming a fixed order.
            const wStart = x.getPixelForValue(window.start);
            const wEnd = x.getPixelForValue(window.end);
            const left = Math.max(Math.min(wStart, wEnd), chartArea.left);
            const right = Math.min(Math.max(wStart, wEnd), chartArea.right);
            ctx.save();
            // Full-window shaded band behind the dots, with dashed edges at the window
            // start/end (edges coinciding with the plot boundary — e.g. mid-point mode —
            // are skipped).
            drawTrendWindowBand(ctx, {left, right, top: chartArea.top, bottom: chartArea.bottom}, [wStart, wEnd], chartArea.left, chartArea.right);
            ctx.restore();
        },
    }), []);

    return (
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Typography variant="caption" sx={{color: 'text.secondary', mb: 0.5, flexShrink: 0}}>
                {temporalView === 'points' && temporalGranularity === 'normal' && `Each dot is a ${eventNoun} plotted at its date and ${entityNoun}. Persistent horizontal bands indicate recurring failure patterns; isolated dots suggest one-off events.`}
                {temporalView === 'points' && temporalGranularity !== 'normal' && `One bubble per ${entityNoun} per ${temporalGranularity}, sized by the number of ${eventNoun}s — larger/brighter bands show where activity concentrates.`}
                {temporalView === 'sparkline' && `One mini line chart per ${entityNoun} of ${sparklineGranularity}ly ${eventNoun} counts. Each row is normalized to its own peak — compare shape (when did this ${entityNoun} spike?), not magnitude (use Dotplot for that).`}
                {temporalView === 'boxplot' && !boxGapMode && `One box per ${entityNoun} showing the spread of its ${eventNoun} dates — the box spans the middle 50% (Q1–Q3), the line is the median, and the whiskers reach the earliest/latest ${eventNoun}. Tall boxes = long-running patterns; short, recent boxes = bursts.`}
                {temporalView === 'boxplot' && boxGapMode && `One box per ${entityNoun} showing the spread of the gap (in days) between its consecutive ${eventNoun}s — low, tight boxes = a regular/frequent pattern; tall boxes = sporadic recurrence. Single-${eventNoun} ${entityNoun}s are omitted.`}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{mb: 0.5, flexShrink: 0}}>
                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>View</Typography>
                <FormControl size="small" sx={{minWidth: 90}}>
                    <Select
                        value={temporalView}
                        onChange={(e): void => {
                            setTemporalView(e.target.value as TemporalView);
                        }}
                        sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                    >
                        <MenuItem value="points" sx={{fontSize: '0.7rem'}}>Dotplot</MenuItem>
                        <MenuItem value="sparkline" sx={{fontSize: '0.7rem'}}>Sparkline</MenuItem>
                        <MenuItem value="boxplot" sx={{fontSize: '0.7rem'}}>Boxplot</MenuItem>
                    </Select>
                </FormControl>
                {(temporalView === 'points' || temporalView === 'sparkline') && (
                    <>
                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 2}}>Group by</Typography>
                        <FormControl size="small" sx={{minWidth: 80}}>
                            <Select
                                // Sparkline can't render "None" (it always buckets), so the "None"
                                // item is hidden for it. Show the effective bucketing instead of the
                                // raw 'normal' value, otherwise the Select renders blank — while
                                // keeping the underlying state so Dotplot's "None" choice is preserved.
                                value={temporalView === 'sparkline' ? sparklineGranularity : temporalGranularity}
                                onChange={(e): void => setTemporalGranularity(e.target.value as TemporalGranularity)}
                                sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                            >
                                {/* Sparkline always needs bucketing, so hide the "None" option for it. */}
                                {temporalView === 'points' && <MenuItem value="normal" sx={{fontSize: '0.7rem'}}>None</MenuItem>}
                                <MenuItem value="month" sx={{fontSize: '0.7rem'}}>Month</MenuItem>
                                <MenuItem value="quarter" sx={{fontSize: '0.7rem'}}>Quarter</MenuItem>
                                <MenuItem value="year" sx={{fontSize: '0.7rem'}}>Year</MenuItem>
                            </Select>
                        </FormControl>
                    </>
                )}
                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 2}}>Sort</Typography>
                <FormControl size="small" sx={{minWidth: 110}}>
                    <Select
                        value={sort}
                        onChange={(e): void => onSortChange(e.target.value as TemporalSort)}
                        sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                    >
                        <MenuItem value="count" sx={{fontSize: '0.7rem'}}>By {entityNoun} size</MenuItem>
                        <MenuItem value="rising-first" disabled={trendWindowMode === 'allTime'} sx={{fontSize: '0.7rem'}}>Rising first</MenuItem>
                        <MenuItem value="most-accelerating" disabled={trendWindowMode === 'allTime'} sx={{fontSize: '0.7rem'}}>Most accelerating</MenuItem>
                        <MenuItem value="falling-first" disabled={trendWindowMode === 'allTime'} sx={{fontSize: '0.7rem'}}>Falling first</MenuItem>
                    </Select>
                </FormControl>
                <Tooltip title={`Hide ${entityNoun}s with fewer than this many ${eventNoun}s inside the trend window (recent + prior halves). Linked to the global Trend window selector in the header — changing the window re-applies the filter.`} arrow>
                    <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', ml: 2, cursor: 'help'}}>Min in window</Typography>
                </Tooltip>
                <FormControl size="small" sx={{minWidth: 60}}>
                    <Select
                        value={minClusterCount}
                        onChange={(e): void => onMinClusterCountChange(Number(e.target.value))}
                        sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                    >
                        <MenuItem value={1} sx={{fontSize: '0.7rem'}}>All</MenuItem>
                        <MenuItem value={2} sx={{fontSize: '0.7rem'}}>≥2</MenuItem>
                        <MenuItem value={3} sx={{fontSize: '0.7rem'}}>≥3</MenuItem>
                        <MenuItem value={5} sx={{fontSize: '0.7rem'}}>≥5</MenuItem>
                        <MenuItem value={10} sx={{fontSize: '0.7rem'}}>≥10</MenuItem>
                    </Select>
                </FormControl>
                {temporalView === 'boxplot' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={boxGapMode}
                                onChange={(e): void => setBoxGapMode(e.target.checked)}
                                size="small"
                                sx={{py: 0}}
                            />
                        }
                        label={<Typography variant="caption" sx={{color: 'text.secondary'}}>Days between {eventNoun}s</Typography>}
                        sx={{ml: 0}}
                    />
                )}
                {(temporalView === 'points' || temporalView === 'sparkline') && (
                    <>
                        <Box sx={{flex: 1}} />
                        <Tooltip title={`Two coloring lenses. "Recent trend" compares each ${entityNoun}'s ${eventNoun} counts in the recent half vs. the prior half of the selected trend window (count-based; needs a window). "Lifetime trend" runs the Laplace centroid test over each ${entityNoun}'s whole history (distribution-free; verdicts deteriorating / improving / stable behind a significance gate — works in any window mode, including All-time).`} arrow>
                            <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help'}}>Color by</Typography>
                        </Tooltip>
                        <FormControl size="small" sx={{minWidth: 120}}>
                            <Select
                                value={colorMode}
                                onChange={(e): void => setColorMode(e.target.value as 'none' | 'window' | 'laplace')}
                                sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                            >
                                <MenuItem value="none" sx={{fontSize: '0.7rem'}}>None</MenuItem>
                                <MenuItem value="window" disabled={trendWindowMode === 'allTime'} sx={{fontSize: '0.7rem'}}>Recent trend</MenuItem>
                                <MenuItem value="laplace" sx={{fontSize: '0.7rem'}}>Lifetime trend</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip title={trendWindowMode === 'allTime'
                            ? 'Not applicable in All-time mode — there is no trend window to filter by'
                            : trendWindowMode === 'midpoint'
                                ? 'Not applicable in Mid-point mode — the window spans the full date range, so every dot is in-window'
                                : 'Hide dots that fall outside the trend window — only in-window activity is shown'} arrow>
                            <span>
                                <FormControlLabel
                                    disabled={trendWindowMode === 'midpoint' || trendWindowMode === 'allTime'}
                                    control={
                                        <Checkbox
                                            checked={hideOutOfWindow && trendWindowMode !== 'midpoint' && trendWindowMode !== 'allTime'}
                                            onChange={(e): void => setHideOutOfWindow(e.target.checked)}
                                            size="small"
                                            sx={{py: 0}}
                                        />
                                    }
                                    label={<Typography variant="caption" sx={{color: 'text.secondary'}}>Hide out-of-window</Typography>}
                                    sx={{ml: 0, mr: 0}}
                                />
                            </span>
                        </Tooltip>
                    </>
                )}
            </Stack>
            {(totalEntityCount > 1 || ((temporalView === 'points' || temporalView === 'sparkline') && colorByTrend)) && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{mb: 0.5, flexShrink: 0}}>
                    {totalEntityCount > 1 && (
                        <>
                            <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                Show top
                            </Typography>
                            <FormControl size="small" sx={{minWidth: 70}}>
                                <Select
                                    // Clamp the displayed value to the "All" option (totalEntityCount) when the
                                    // chosen "top N" exceeds the available entities — otherwise the Select has a
                                    // value (e.g. the default 30) with no matching MenuItem and renders blank.
                                    // The slice already treats N >= total as "show all", so this is display-only.
                                    value={temporalRangeMode === 'custom' ? 'custom' : Math.min(temporalRangeMode, totalEntityCount)}
                                    onChange={(e): void => {
                                        const v = e.target.value;
                                        if (v === 'custom') {
                                            setTemporalRangeMode('custom');
                                        } else {
                                            setTemporalRangeMode(v as number);
                                        }
                                    }}
                                    sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                                >
                                    {[30, 50, 100, 200, 500].filter((v) => v < totalEntityCount).map((v) => (
                                        <MenuItem key={v} value={v} sx={{fontSize: '0.7rem'}}>{v}</MenuItem>
                                    ))}
                                    <MenuItem value={totalEntityCount} sx={{fontSize: '0.7rem'}}>All</MenuItem>
                                    <MenuItem value="custom" sx={{fontSize: '0.7rem'}}>Custom</MenuItem>
                                </Select>
                            </FormControl>
                            {temporalRangeMode !== 'custom' && (
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                    of {totalEntityCount} {entityNoun}s{filtered.length < totalEntityCount ? ` (${filtered.length}/${totalEntityCount} after filters)` : ''}
                                </Typography>
                            )}
                            {temporalRangeMode === 'custom' && (
                                <>
                                    <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>| from</Typography>
                                    <FormControl size="small" sx={{minWidth: 60}}>
                                        <Select
                                            value={temporalStart}
                                            onChange={(e): void => {
                                                const v = e.target.value as number;
                                                setTemporalStart(v);
                                                if (v > temporalEnd) setTemporalEnd(Math.min(v + 29, totalEntityCount));
                                            }}
                                            sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                                        >
                                            {Array.from({length: totalEntityCount}, (_, i) => i + 1).map((v) => (
                                                <MenuItem key={v} value={v} sx={{fontSize: '0.7rem'}}>{v}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>to</Typography>
                                    <FormControl size="small" sx={{minWidth: 60}}>
                                        <Select
                                            value={Math.min(temporalEnd, totalEntityCount)}
                                            onChange={(e): void => setTemporalEnd(e.target.value as number)}
                                            sx={{fontSize: '0.7rem', height: 22, '& .MuiSelect-select': {py: 0, px: 1}}}
                                        >
                                            {Array.from({length: totalEntityCount - temporalStart + 1}, (_, i) => temporalStart + i).map((v) => (
                                                <MenuItem key={v} value={v} sx={{fontSize: '0.7rem'}}>{v}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                        of {totalEntityCount}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                    {(temporalView === 'points' || temporalView === 'sparkline') && colorByTrend && (
                        <>
                            <Box sx={{flex: 1}} />
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>Trend</Typography>
                                {(['rising', 'stable', 'falling'] as const).map((trend) => {
                                    const active = temporalTrendFilter.has(trend);
                                    const anyActive = temporalTrendFilter.size > 0;
                                    const label = colorMode === 'laplace'
                                        ? (trend === 'rising' ? 'Deteriorating' : trend === 'stable' ? 'Stable' : 'Improving')
                                        : (trend === 'rising' ? 'Rising' : trend === 'stable' ? 'Stable' : 'Falling');
                                    const dot = TREND_COLORS[trend];
                                    return (
                                        <Box
                                            key={trend}
                                            onClick={(): void => setTemporalTrendFilter((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(trend)) next.delete(trend);
                                                else next.add(trend);
                                                return next;
                                            })}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 0.75,
                                                py: 0.25,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: active ? dot : 'transparent',
                                                backgroundColor: active ? hexToRgba(dot, 0.12) : 'transparent',
                                                // When chips are filtering and this one isn't selected, fade it
                                                // so the active filter state is visually obvious.
                                                opacity: anyActive && !active ? 0.45 : 1,
                                                transition: 'background-color 80ms, border-color 80ms, opacity 80ms',
                                                '&:hover': {backgroundColor: hexToRgba(dot, 0.18)},
                                            }}
                                        >
                                            <Box component="span" sx={{display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: dot, mr: 0.5}} />
                                            <Typography variant="caption" sx={{color: 'text.secondary', fontWeight: active ? 600 : 400}}>{label}</Typography>
                                        </Box>
                                    );
                                })}
                                {temporalTrendFilter.size > 0 && (
                                    <Tooltip title="Clear trend filter" arrow>
                                        <Typography
                                            onClick={(): void => setTemporalTrendFilter(new Set())}
                                            sx={{fontSize: '0.7rem', color: 'text.secondary', cursor: 'pointer', textDecoration: 'underline', userSelect: 'none'}}
                                        >
                                            clear
                                        </Typography>
                                    </Tooltip>
                                )}
                            </Stack>
                        </>
                    )}
                </Stack>
            )}
            <Box
                sx={{flexGrow: 1, minHeight: 0, overflow: 'hidden'}}
                // Chart.js's onHover only fires while the cursor is over the canvas, so
                // a fast mouse-out can leave the highlight stuck. Catch the DOM-level
                // mouseleave here and clear the hover state explicitly.
                onMouseLeave={(): void => onHoverKey(null)}
            >
                {temporalView === 'points' && temporalGranularity === 'normal' && <Scatter ref={temporalChartRef} data={temporalData} options={temporalOptions} plugins={[trendWindowPlugin, hoverDimPlugin]} />}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {temporalView === 'points' && temporalGranularity !== 'normal' && <Scatter ref={temporalChartRef} data={temporalDensityData as any} options={temporalOptions} plugins={[trendWindowPlugin, hoverDimPlugin]} />}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {temporalView === 'sparkline' && <Scatter ref={temporalChartRef} data={temporalSparklineData as any} options={temporalOptions} plugins={[trendWindowPlugin, hoverDimPlugin]} />}
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {temporalView === 'boxplot' && <Scatter data={temporalBoxBaseData as any} options={temporalBoxOptions as any} plugins={[boxPlugin]} />}
            </Box>
        </Box>
    );
};

export default TemporalDistributionPanel;
